import type { z } from "zod";
import type {
  Asset,
  ChapterSchema,
  ChapterContent,
  DeleteDocumentChapterResponseSchema,
  CommentReply,
  CommentThread,
  ForumSession,
  ForumUser,
  DiceRollResult,
  DocumentEnvelope,
  DocumentAccess,
  DocumentAccessEntry,
  DocumentAclPermission,
  DocumentVisibility,
  DeleteDocumentAccessResponse,
  EmojiCatalogSchema,
  RevisionPage,
  AttachmentSchema,
  PollSchema,
  PollVotePageSchema,
  PurchaseAttachmentResponseSchema,
  ResolveMentionResponseSchema,
  ResolveReplyGateResponseSchema,
  SuggestionBatchSchema,
  SuggestionSchema,
  DocumentListItem,
  DocumentTag,
  Tag,
} from "./schemas.js";

/** 非 2xx 响应抛出的类型化错误。 */
export class ApiClientError extends Error {
  /** HTTP 状态码。 */
  readonly status: number;
  /** 服务端稳定错误代码。 */
  readonly code: string;
  /** 可用于冲突处理等 UI 的结构化详情。 */
  readonly details: Record<string, unknown> | undefined;

  /** 创建 API 客户端错误。 */
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** 创建客户端时可注入的 fetch 与论坛身份选项。 */
export interface ApiClientOptions {
  /** API 根地址；浏览器同源代理时保持空字符串。 */
  baseUrl?: string;
  /** 开发环境写入 `x-user-id` 的身份 ID；传函数时每个请求动态解析。 */
  userId?: string | (() => string | undefined);
  /** 可注入 mock fetch 或宿主自定义网络实现。 */
  fetch?: typeof fetch;
}

/** 由共享契约约束的浏览器 API 客户端。 */
export interface RiceTextApiClient {
  /** 读取当前登录身份可选择的文章。 */
  listDocuments(signal?: AbortSignal): Promise<{ items: DocumentListItem[] }>;
  /** 读取文档当前 revision 和 Tiptap JSON。 */
  getDocument(documentId: string, signal?: AbortSignal): Promise<DocumentEnvelope>;
  /** 读取 owner、公开状态和完整 ACL；仅访问管理员可用。 */
  getDocumentAccess(documentId: string, signal?: AbortSignal): Promise<DocumentAccess>;
  /** 修改文章公开/私有状态，不产生正文修订。 */
  updateDocumentVisibility(
    documentId: string,
    visibility: DocumentVisibility,
    signal?: AbortSignal,
  ): Promise<DocumentAccess>;
  /** 新增或更新一个 ACL 用户。 */
  upsertDocumentAccess(
    documentId: string,
    userId: string,
    permission: DocumentAclPermission,
    signal?: AbortSignal,
  ): Promise<DocumentAccessEntry>;
  /** 幂等撤销一个非 owner ACL 用户。 */
  deleteDocumentAccess(
    documentId: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<DeleteDocumentAccessResponse>;
  /** 按 baseRevision 乐观并发更新正文。 */
  updateDocument(
    documentId: string,
    body: {
      schemaVersion: number;
      baseRevision: number;
      clientMutationId: string;
      title?: string;
      content: DocumentEnvelope["content"];
    },
    signal?: AbortSignal,
  ): Promise<DocumentEnvelope>;
  /** 使用 ProseMirror 增量 steps 更新文档（服务端完整应用）。 */
  updateDocumentSteps(
    documentId: string,
    body: {
      schemaVersion: number;
      baseRevision: number;
      clientMutationId: string;
      steps: Array<Record<string, unknown>>;
      chapterId?: string;
    },
    signal?: AbortSignal,
  ): Promise<DocumentEnvelope>;
  /** 注册正文中出现、但目录缺失的新章节；身份由调用方提供，位置由服务端追加。 */
  createDocumentChapter(
    documentId: string,
    body: { title: string; chapterId?: string; order?: number },
    signal?: AbortSignal,
  ): Promise<z.infer<typeof ChapterSchema>>;
  /** 删除章节目录行（幂等）；历史修订不受影响。 */
  deleteDocumentChapter(
    documentId: string,
    chapterId: string,
    signal?: AbortSignal,
  ): Promise<z.infer<typeof DeleteDocumentChapterResponseSchema>>;
  /** 隐藏/恢复章节目录行；隐藏的章节读者不可读。 */
  updateDocumentChapter(
    documentId: string,
    chapterId: string,
    body: { hidden: boolean },
    signal?: AbortSignal,
  ): Promise<z.infer<typeof ChapterSchema>>;
  /** 对比章节内容哈希，返回需要上传与已存在的章节 ID。 */
  syncNovelChapters(
    novelId: string,
    chapters: Array<{ id: string; title: string; order: number; hash: string }>,
    signal?: AbortSignal,
  ): Promise<{ toUpdate: string[]; existing: string[] }>;
  /** 读取单个章节正文。 */
  getNovelChapter(
    novelId: string,
    chapterId: string,
    signal?: AbortSignal,
  ): Promise<ChapterContent>;
  saveNovelChapter(
    novelId: string,
    chapterId: string,
    input: {
      title: string;
      order: number;
      content: DocumentEnvelope["content"];
      hash: string;
      baseRevision: number;
    },
    signal?: AbortSignal,
  ): Promise<{
    id: string;
    title: string;
    order: number;
    revision: number;
    /** saved = 本次实际写入；unchanged = content_hash 一致，未递增版本。 */
    status: "saved" | "unchanged";
  }>;
  /** 批量保存章节正文（每批最多 20 章；content_hash 一致时返回 unchanged 不递增版本）。 */
  saveNovelChaptersBatch(
    novelId: string,
    body: {
      chapters: Array<{
        id: string;
        title: string;
        order: number;
        content: DocumentEnvelope["content"];
        hash: string;
        baseRevision: number;
      }>;
    },
    signal?: AbortSignal,
  ): Promise<{
    chapters: Array<{
      id: string;
      title: string;
      order: number;
      revision: number;
      status: "saved" | "unchanged";
    }>;
  }>;
  /** 创建或恢复一个不影响线上目录的整本上传会话。 */
  createChapterUpload(
    novelId: string,
    body: { manifestHash: string; totalChapters: number },
    signal?: AbortSignal,
  ): Promise<{
    uploadId: string;
    manifestHash: string;
    totalChapters: number;
    status: "uploading" | "published";
    staged: string[];
  }>;
  /** 把一个正文批次写入上传会话暂存区。 */
  stageChapterUploadBatch(
    novelId: string,
    uploadId: string,
    body: {
      chapters: Array<{
        id: string;
        title: string;
        order: number;
        content: DocumentEnvelope["content"];
        hash: string;
        baseRevision: number;
      }>;
    },
    signal?: AbortSignal,
  ): Promise<{
    chapters: Array<{
      id: string;
      title: string;
      order: number;
      revision: number;
      status: "saved" | "unchanged";
    }>;
  }>;
  /** 完整验收后原子替换线上章节。 */
  completeChapterUpload(
    novelId: string,
    uploadId: string,
    signal?: AbortSignal,
  ): Promise<{
    uploadId: string;
    manifestHash: string;
    totalChapters: number;
    publishedAt: string;
  }>;
  /**
   * 换序暂存（每批最多 40 项，不发送正文）。
   *
   * 数组顺序就是目标顺序，临时位置由服务端分配；章节身份与正文都不变。
   */
  stageNovelChapterReorder(
    novelId: string,
    body: {
      chapters: Array<{
        id: string;
        /** @deprecated 位置由服务端按数组顺序分配。 */
        temporaryOrder?: number;
        baseRevision: number;
      }>;
    },
    signal?: AbortSignal,
  ): Promise<{
    chapters: Array<{
      id: string;
      revision: number;
      status: "staged" | "unchanged";
    }>;
  }>;
  /** 游标分页读取不可变版本历史。 */
  listRevisions(
    documentId: string,
    chapterId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<RevisionPage>;
  /** 按章节版本读取对应的不可变文档快照。 */
  getRevision(
    documentId: string,
    revision: number,
    chapterId: string,
    signal?: AbortSignal,
  ): Promise<DocumentEnvelope>;
  /** 复制指定历史快照并创建新的回滚 revision。 */
  rollbackDocument(
    documentId: string,
    body: {
      baseRevision: number;
      chapterId: string;
      targetRevision: number;
      clientMutationId: string;
    },
    signal?: AbortSignal,
  ): Promise<DocumentEnvelope>;
  /** 使用 multipart 上传图片二进制。 */
  uploadAsset(file: File, signal?: AbortSignal): Promise<Asset>;
  /** 读取站点内置表情目录（分组 + 条目）。 */
  listEmojiCatalog(signal?: AbortSignal): Promise<z.infer<typeof EmojiCatalogSchema>>;
  /** 创建并持久化一次新骰子结果；传 rerollOf 时服务端创建关联旧结果的重投。 */
  createDice(
    expression: string,
    rerollOf?: string | null,
    signal?: AbortSignal,
  ): Promise<DiceRollResult>;
  /** 按 rollId 读取稳定结果，不触发重投。 */
  getDice(rollId: string, signal?: AbortSignal): Promise<DiceRollResult>;
  /** 显式重投并创建关联旧结果的新 rollId。 */
  rerollDice(rollId: string, signal?: AbortSignal): Promise<DiceRollResult>;
  /** 按锚点读取排序后的间贴树。 */
  getCommentThread(
    documentId: string,
    anchorId: string,
    sort?: "score" | "newest",
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<CommentThread>;
  /** 创建根回复或指定 parentId 的楼中楼。 */
  createCommentReply(
    documentId: string,
    anchorId: string,
    body: string,
    parentId?: string,
    signal?: AbortSignal,
  ): Promise<CommentReply>;
  /** 设置赞、踩或 0 撤销，并返回权威计数。 */
  voteComment(
    replyId: string,
    value: -1 | 0 | 1,
    signal?: AbortSignal,
  ): Promise<{
    score: number;
    viewerVote: -1 | 0 | 1;
    upvotes: number;
    downvotes: number;
    myVote: -1 | 0 | 1;
  }>;
  /** 读取当前和可切换的论坛身份。 */
  getForumSession(signal?: AbortSignal): Promise<ForumSession>;
  /** 读取章节目录（按 order 排序，含每章独立版本号）。 */
  listChapters(
    documentId: string,
    signal?: AbortSignal,
  ): Promise<{ items: Array<z.infer<typeof ChapterSchema>> }>;
  /** 按名称或 ID 搜索好友/用户。 */
  searchUsers(
    query: string,
    friendsOnly?: boolean,
    signal?: AbortSignal,
  ): Promise<{ items: ForumUser[] }>;
  /** 在发布前由服务端解析非好友 mention。 */
  resolveMention(
    name: string,
    userId?: string,
    signal?: AbortSignal,
  ): Promise<z.infer<typeof ResolveMentionResponseSchema>>;
  /** 按当前身份投影回复可见内容。 */
  resolveReplyGate(
    gateId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<z.infer<typeof ResolveReplyGateResponseSchema>>;
  /** 读取当前身份可见的章节纠错建议。 */
  listSuggestions(
    documentId: string,
    signal?: AbortSignal,
  ): Promise<{ items: Array<z.infer<typeof SuggestionSchema>> }>;
  /** 提交一条带章节和行定位的待审核纠错建议。 */
  createSuggestion(
    documentId: string,
    body: {
      fromText: string;
      toText: string;
      reason: string;
      chapterId: string;
      chapterTitle: string;
      lineNo: number;
      lineText: string;
    },
    signal?: AbortSignal,
  ): Promise<z.infer<typeof SuggestionSchema>>;
  /** 审核纠错建议；approve 在服务端章节范围核对行上下文后唯一替换，旧建议须全文唯一；定位失效或歧义返回 409。 */
  reviewSuggestion(
    suggestionId: string,
    body: { decision: "approve" | "reject"; baseRevision: number },
    signal?: AbortSignal,
  ): Promise<{
    suggestion: z.infer<typeof SuggestionSchema>;
    document: DocumentEnvelope | null;
  }>;
  /** 读取整章多处修改合并成的校订批次。 */
  listSuggestionBatches(
    documentId: string,
    signal?: AbortSignal,
  ): Promise<{ items: Array<z.infer<typeof SuggestionBatchSchema>> }>;
  /** 提交一个整章校订批次。 */
  createSuggestionBatch(
    documentId: string,
    body: {
      baseRevision: number;
      chapterId: string;
      chapterTitle: string;
      beforeContent: DocumentEnvelope["content"];
      afterContent: DocumentEnvelope["content"];
      steps: Array<Record<string, unknown>>;
      reason: string;
    },
    signal?: AbortSignal,
  ): Promise<z.infer<typeof SuggestionBatchSchema>>;
  /** 原子审核整章校订批次。 */
  reviewSuggestionBatch(
    batchId: string,
    body: { decision: "approve" | "reject"; baseRevision: number },
    signal?: AbortSignal,
  ): Promise<{
    batch: z.infer<typeof SuggestionBatchSchema>;
    document: DocumentEnvelope | null;
  }>;
  /** 读取附件价格和当前购买权益。 */
  getAttachment(id: string, signal?: AbortSignal): Promise<z.infer<typeof AttachmentSchema>>;
  /** 幂等购买附件并执行金币分账。 */
  purchaseAttachment(
    id: string,
    signal?: AbortSignal,
  ): Promise<z.infer<typeof PurchaseAttachmentResponseSchema>>;
  /** 读取投票资格、选项和汇总计数。 */
  getPoll(id: string, signal?: AbortSignal): Promise<z.infer<typeof PollSchema>>;
  /** 提交或覆盖当前身份的投票选择。 */
  submitPollVote(
    id: string,
    optionIds: string[],
    signal?: AbortSignal,
  ): Promise<z.infer<typeof PollSchema>>;
  /** 游标分页读取实名投票明细。 */
  listPollVotes(
    id: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<z.infer<typeof PollVotePageSchema>>;
  /** 读取整篇文章的标签（与章节无关）。 */
  listDocumentTags(documentId: string, signal?: AbortSignal): Promise<{ items: DocumentTag[] }>;
  /** 全量替换文章标签；来源由服务端按站点字典解析。 */
  updateDocumentTags(
    documentId: string,
    body: { items: Array<{ label: string }> },
    signal?: AbortSignal,
  ): Promise<{ items: DocumentTag[] }>;
  /** 读取站点标签字典（不含隐藏条目）。 */
  listServerTags(signal?: AbortSignal): Promise<{ items: Tag[] }>;
  /** 新建站点标签（仅版主）。 */
  createServerTag(
    body: { label: string; description?: string },
    signal?: AbortSignal,
  ): Promise<Tag>;
  /** 维护站点标签（仅版主）；slug 不可改。 */
  updateServerTag(
    tagId: string,
    body: { label?: string; description?: string; hidden?: boolean },
    signal?: AbortSignal,
  ): Promise<Tag>;
}

/** 创建零依赖的类型化 fetch 客户端。 */
export function createApiClient(options: ApiClientOptions = {}): RiceTextApiClient {
  const baseUrl = (options.baseUrl ?? "").replace(/\/$/, "");
  const fetcher = options.fetch ?? fetch;
  type ClientRequestInit = Omit<RequestInit, "signal"> & {
    signal?: AbortSignal | undefined;
  };
  // 所有方法共享错误解包路径，保证调用方只处理 ApiClientError 而非各类 Response。
  const request = async <T>(path: string, init: ClientRequestInit = {}): Promise<T> => {
    const headers = new Headers(init.headers);
    const userId = typeof options.userId === "function" ? options.userId() : options.userId;
    if (userId) headers.set("x-user-id", userId);
    if (init.body && !(init.body instanceof FormData))
      headers.set("content-type", "application/json");
    const { signal, ...requestInit } = init;
    const response = await fetcher(`${baseUrl}${path}`, {
      credentials: "include",
      ...requestInit,
      headers,
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: {
          code?: string;
          message?: string;
          details?: Record<string, unknown>;
        };
      } | null;
      throw new ApiClientError(
        response.status,
        payload?.error?.code ?? "HTTP_ERROR",
        payload?.error?.message ?? response.statusText,
        payload?.error?.details,
      );
    }
    return (await response.json()) as T;
  };
  const json = (value: unknown): string => JSON.stringify(value);
  // 只编码已提供参数，避免把 undefined 传成字符串并破坏游标语义。
  const query = (entries: Record<string, string | number | boolean | undefined>): string => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(entries))
      if (value !== undefined) params.set(key, String(value));
    const encoded = params.toString();
    return encoded ? `?${encoded}` : "";
  };
  return {
    listDocuments: (signal) => request("/api/documents", { signal }),
    getDocument: (id, signal) => request(`/api/documents/${id}`, { signal }),
    getDocumentAccess: (id, signal) => request(`/api/documents/${id}/access`, { signal }),
    updateDocumentVisibility: (id, visibility, signal) =>
      request(`/api/documents/${id}/access`, {
        method: "PATCH",
        body: json({ visibility }),
        signal,
      }),
    upsertDocumentAccess: (id, userId, permission, signal) =>
      request(`/api/documents/${id}/access/users/${userId}`, {
        method: "PUT",
        body: json({ permission }),
        signal,
      }),
    deleteDocumentAccess: (id, userId, signal) =>
      request(`/api/documents/${id}/access/users/${userId}`, {
        method: "DELETE",
        signal,
      }),
    updateDocument: (id, body, signal) =>
      request(`/api/documents/${id}`, {
        method: "PUT",
        body: json(body),
        signal,
      }),
    updateDocumentSteps: (id, body, signal) =>
      request(`/api/documents/${id}/steps`, {
        method: "PATCH",
        body: json(body),
        signal,
      }),
    createDocumentChapter: (id, body, signal) =>
      request(`/api/documents/${id}/chapters`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    deleteDocumentChapter: (id, chapterId, signal) =>
      request(`/api/documents/${id}/chapters/${chapterId}`, {
        method: "DELETE",
        signal,
      }),
    updateDocumentChapter: (id, chapterId, body, signal) =>
      request(`/api/documents/${id}/chapters/${chapterId}`, {
        method: "PATCH",
        body: json(body),
        signal,
      }),
    syncNovelChapters: (novelId, chapters, signal) =>
      request(`/api/forum/novels/${novelId}/chapters/sync`, {
        method: "POST",
        body: json({ chapters }),
        signal,
      }),
    getNovelChapter: (novelId, chapterId, signal) =>
      request(`/api/forum/novels/${novelId}/chapters/${chapterId}`, { signal }),
    saveNovelChapter: (novelId, chapterId, input, signal) =>
      request(`/api/forum/novels/${novelId}/chapters/${chapterId}`, {
        method: "PUT",
        body: json(input),
        signal,
      }),
    saveNovelChaptersBatch: (novelId, body, signal) =>
      request(`/api/forum/novels/${novelId}/chapters/batch`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    createChapterUpload: (novelId, body, signal) =>
      request(`/api/forum/novels/${novelId}/chapter-uploads`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    stageChapterUploadBatch: (novelId, uploadId, body, signal) =>
      request(`/api/forum/novels/${novelId}/chapter-uploads/${uploadId}/batch`, {
        method: "PUT",
        body: json(body),
        signal,
      }),
    completeChapterUpload: (novelId, uploadId, signal) =>
      request(`/api/forum/novels/${novelId}/chapter-uploads/${uploadId}/complete`, {
        method: "POST",
        signal,
      }),
    stageNovelChapterReorder: (novelId, body, signal) =>
      request(`/api/forum/novels/${novelId}/chapters/reorder-stage`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    listRevisions: (id, chapterId, cursor, signal) =>
      request(`/api/documents/${id}/revisions${query({ cursor, chapterId })}`, {
        signal,
      }),
    getRevision: (id, revision, chapterId, signal) =>
      request(`/api/documents/${id}/revisions/${revision}${query({ chapterId })}`, { signal }),
    rollbackDocument: (id, body, signal) =>
      request(`/api/documents/${id}/rollback`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    uploadAsset: (file, signal) => {
      const form = new FormData();
      form.set("file", file);
      return request("/api/assets", { method: "POST", body: form, signal });
    },
    listEmojiCatalog: (signal) => request("/api/emoji", { signal }),
    createDice: (expression, rerollOf, signal) =>
      request("/api/dice", {
        method: "POST",
        body: json({ expression, ...(rerollOf ? { rerollOf } : {}) }),
        signal,
      }),
    getDice: (id, signal) => request(`/api/dice/${id}`, { signal }),
    rerollDice: (id, signal) => request(`/api/dice/${id}/reroll`, { method: "POST", signal }),
    getCommentThread: (documentId, anchorId, sort = "score", cursor, signal) =>
      request(`/api/documents/${documentId}/comments/${anchorId}${query({ sort, cursor })}`, {
        signal,
      }),
    createCommentReply: (documentId, anchorId, body, parentId, signal) =>
      request(`/api/documents/${documentId}/comments/${anchorId}/replies`, {
        method: "POST",
        body: json({ body, parentId: parentId ?? null }),
        signal,
      }),
    voteComment: (replyId, value, signal) =>
      request(`/api/comments/replies/${replyId}/vote`, {
        method: "PUT",
        body: json({ value }),
        signal,
      }),
    getForumSession: (signal) => request("/api/forum/session", { signal }),
    listChapters: (documentId, signal) =>
      request(`/api/forum/chapters${query({ documentId })}`, { signal }),
    searchUsers: (q, friendsOnly = false, signal) =>
      request(`/api/forum/users/search${query({ q, friendsOnly })}`, {
        signal,
      }),
    resolveMention: (name, userId, signal) =>
      request("/api/forum/mentions/resolve", {
        method: "POST",
        body: json({ name, ...(userId ? { userId } : {}) }),
        signal,
      }),
    resolveReplyGate: (gateId, documentId, signal) =>
      request("/api/forum/reply-gates/resolve", {
        method: "POST",
        body: json({ gateId, documentId }),
        signal,
      }),
    listSuggestions: (id, signal) => request(`/api/forum/documents/${id}/suggestions`, { signal }),
    createSuggestion: (id, body, signal) =>
      request(`/api/forum/documents/${id}/suggestions`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    reviewSuggestion: (id, body, signal) =>
      request(`/api/forum/suggestions/${id}`, {
        method: "PATCH",
        body: json(body),
        signal,
      }),
    listSuggestionBatches: (id, signal) =>
      request(`/api/forum/documents/${id}/suggestion-batches`, { signal }),
    createSuggestionBatch: (id, body, signal) =>
      request(`/api/forum/documents/${id}/suggestion-batches`, {
        method: "POST",
        body: json(body),
        signal,
      }),
    reviewSuggestionBatch: (id, body, signal) =>
      request(`/api/forum/suggestion-batches/${id}`, {
        method: "PATCH",
        body: json(body),
        signal,
      }),
    getAttachment: (id, signal) => request(`/api/forum/attachments/${id}`, { signal }),
    purchaseAttachment: (id, signal) =>
      request(`/api/forum/attachments/${id}/purchase`, {
        method: "POST",
        signal,
      }),
    getPoll: (id, signal) => request(`/api/forum/polls/${id}`, { signal }),
    submitPollVote: (id, optionIds, signal) =>
      request(`/api/forum/polls/${id}/votes`, {
        method: "POST",
        body: json({ optionIds }),
        signal,
      }),
    listPollVotes: (id, cursor, signal) =>
      request(`/api/forum/polls/${id}/votes${query({ cursor })}`, { signal }),
    listDocumentTags: (documentId, signal) =>
      request(`/api/documents/${documentId}/tags`, { signal }),
    updateDocumentTags: (documentId, body, signal) =>
      request(`/api/documents/${documentId}/tags`, {
        method: "PUT",
        body: json(body),
        signal,
      }),
    listServerTags: (signal) => request("/api/forum/tags", { signal }),
    createServerTag: (body, signal) =>
      request("/api/forum/tags", { method: "POST", body: json(body), signal }),
    updateServerTag: (tagId, body, signal) =>
      request(`/api/forum/tags/${tagId}`, {
        method: "PATCH",
        body: json(body),
        signal,
      }),
  };
}
